import type { Fetcher } from "../fetcher";
import type { ApiSingleResponse, ApiCollectionResponse } from "../types/api";
import type {
  ClientType,
  EntityRating,
  Currency,
  VatRate,
} from "../entities/reference";
import type { Department } from "../entities/company";
import type { User } from "../entities/user";
import type {
  CreateDepartmentDto,
  UpdateDepartmentDto,
  CreateClientTypeDto,
  UpdateClientTypeDto,
  CreateEntityRatingDto,
  UpdateEntityRatingDto,
  CreateCurrencyDto,
  UpdateCurrencyDto,
  CreateVatRateDto,
  UpdateVatRateDto,
  CreateTenantUserDto,
  UpdateTenantUserDto,
  UserStats,
  Organization,
  UpdateOrganizationDto,
} from "./types";

export function createTenantRepository(fetcher: Fetcher) {
  return {
    // ─── Departments ────────────────────────────────────────────────────
    async listDepartments(): Promise<{ items: Department[] }> {
      const response = await fetcher<ApiCollectionResponse<Department>>("/departments");
      return { items: response.data?.items ?? [] };
    },
    async createDepartment(payload: CreateDepartmentDto): Promise<Department> {
      const response = await fetcher<ApiSingleResponse<Department>>("/departments", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return response.data;
    },
    async updateDepartment(id: string, payload: UpdateDepartmentDto): Promise<Department> {
      const response = await fetcher<ApiSingleResponse<Department>>(`/departments/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      return response.data;
    },
    async deleteDepartment(id: string): Promise<void> {
      await fetcher(`/departments/${id}`, { method: "DELETE" });
    },

    // ─── Client Types ───────────────────────────────────────────────────
    async listClientTypes(): Promise<{ items: ClientType[] }> {
      const response = await fetcher<ApiCollectionResponse<ClientType>>("/client-types");
      return { items: response.data?.items ?? [] };
    },
    async createClientType(payload: CreateClientTypeDto): Promise<ClientType> {
      const response = await fetcher<ApiSingleResponse<ClientType>>("/client-types", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return response.data;
    },
    async updateClientType(id: string, payload: UpdateClientTypeDto): Promise<ClientType> {
      const response = await fetcher<ApiSingleResponse<ClientType>>(`/client-types/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      return response.data;
    },
    async deleteClientType(id: string): Promise<void> {
      await fetcher(`/client-types/${id}`, { method: "DELETE" });
    },

    // ─── Entity Ratings ─────────────────────────────────────────────────
    async listEntityRatings(): Promise<{ items: EntityRating[] }> {
      const response = await fetcher<ApiCollectionResponse<EntityRating>>("/entity-ratings");
      return { items: response.data?.items ?? [] };
    },
    async createEntityRating(payload: CreateEntityRatingDto): Promise<EntityRating> {
      const response = await fetcher<ApiSingleResponse<EntityRating>>("/entity-ratings", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return response.data;
    },
    async updateEntityRating(
      id: string,
      payload: UpdateEntityRatingDto,
    ): Promise<EntityRating> {
      const response = await fetcher<ApiSingleResponse<EntityRating>>(
        `/entity-ratings/${id}`,
        { method: "PATCH", body: JSON.stringify(payload) },
      );
      return response.data;
    },
    async deleteEntityRating(id: string): Promise<void> {
      await fetcher(`/entity-ratings/${id}`, { method: "DELETE" });
    },

    // ─── Currencies ─────────────────────────────────────────────────────
    async listCurrencies(): Promise<{ items: Currency[] }> {
      const response = await fetcher<ApiCollectionResponse<Currency>>("/currencies");
      return { items: response.data?.items ?? [] };
    },
    async createCurrency(payload: CreateCurrencyDto): Promise<Currency> {
      const response = await fetcher<ApiSingleResponse<Currency>>("/currencies", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return response.data;
    },
    async updateCurrency(id: string, payload: UpdateCurrencyDto): Promise<Currency> {
      const response = await fetcher<ApiSingleResponse<Currency>>(`/currencies/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      return response.data;
    },
    async deleteCurrency(id: string): Promise<void> {
      await fetcher(`/currencies/${id}`, { method: "DELETE" });
    },

    // ─── VAT Rates ──────────────────────────────────────────────────────
    async listVatRates(): Promise<{ items: VatRate[] }> {
      const response = await fetcher<ApiCollectionResponse<VatRate>>("/vat-rates");
      return { items: response.data?.items ?? [] };
    },
    async createVatRate(payload: CreateVatRateDto): Promise<VatRate> {
      const response = await fetcher<ApiSingleResponse<VatRate>>("/vat-rates", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return response.data;
    },
    async updateVatRate(id: string, payload: UpdateVatRateDto): Promise<VatRate> {
      const response = await fetcher<ApiSingleResponse<VatRate>>(`/vat-rates/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      return response.data;
    },
    async deleteVatRate(id: string): Promise<void> {
      await fetcher(`/vat-rates/${id}`, { method: "DELETE" });
    },

    // ─── Users ──────────────────────────────────────────────────────────
    async listUsers(): Promise<{ items: User[] }> {
      const response = await fetcher<ApiCollectionResponse<User>>("/users");
      return { items: response.data?.items ?? [] };
    },
    async createUser(payload: CreateTenantUserDto): Promise<User> {
      const response = await fetcher<ApiSingleResponse<User>>("/users", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return response.data;
    },
    async updateUser(id: string, payload: UpdateTenantUserDto): Promise<User> {
      const response = await fetcher<ApiSingleResponse<User>>(`/users/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      return response.data;
    },
    async deleteUser(id: string): Promise<void> {
      await fetcher(`/users/${id}`, { method: "DELETE" });
    },
    async getUserStats(id: string): Promise<UserStats> {
      const response = await fetcher<ApiSingleResponse<UserStats>>(`/users/${id}/stats`);
      return response.data;
    },

    // ─── Organization (current company) ─────────────────────────────────
    async getOrganization(): Promise<Organization> {
      const response = await fetcher<ApiSingleResponse<Organization>>("/organization");
      return response.data;
    },
    async updateOrganization(payload: UpdateOrganizationDto): Promise<Organization> {
      const response = await fetcher<ApiSingleResponse<Organization>>("/organization", {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      return response.data;
    },
  };
}
